import { useEffect, useState } from "react";
import CustomerPicker from "../document/CustomerPicker";
import DocumentInformation, { type DocumentFieldConfig } from "../document/DocumentInformation";
import DeliveryOrderItemTable from "../deliveryOrder/DeliveryOrderItemTable";
import type { CustomerOption } from "../../api/customerApi";
import { getQuotation, getQuotationById } from "../../api/quotationApi";
import { getSales } from "../../api/salesApi";
import { isForbiddenError } from "../../lib/axios";
import { getApiErrorMessage } from "../../lib/httpError";
import { isSalesEditLocked } from "../../lib/roleAccess";
import { showError } from "../../utils/ alert";
import {
  type FieldErrors,
  type ItemsErrors,
  focusFirstError,
  withErrorClear,
} from "../../helpers/validation";
import { type DosFormItem, validateDosForm } from "../../helpers/dosValidation";
import type { DosPayload } from "../../type/dos";

/** Quotation berstatus ini tidak boleh dipakai untuk DOS (convert akan ditolak). */
const QUOTATION_CANCELLED_CODE = "CANCELLED";

export interface DosFormValue {
  customer: {
    id: number;
    customer_name: string;
    company_name: string | null;
    address: string | null;
    phone: string | null;
  } | null;
  sales_id: number | null;
  po_number: string;
  delivery_date: string;
  due_date: string;
  checked_by: string;
  received_by: string;
  quotation: { id: number; quotation_no: string } | null;
  items: DosFormItem[];
}

interface QuotationOption {
  id: number;
  quotation_no: string;
  recipient_name?: string | null;
  status: { code: string } | null;
}

interface SalesOption {
  id: number;
  name: string;
}

interface DocumentValue {
  bill_to: string;
  address: string;
  phone: string;
  delivery_date: string;
  due_date: string;
  sales_id: number | "";
  po_number: string;
}

interface DosFormProps {
  title: string;
  initialValue?: DosFormValue;
  rejectionReason?: string | null;
  onSubmit: (payload: DosPayload) => Promise<void>;
  /** Dipanggil setelah pesan error simpan ditampilkan (mis. edit -> detail setelah 409). */
  onSubmitError?: (error: unknown) => void;
  onCancel: () => void;
}

const inputClass =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400";

const customerLabel = (name: string, company?: string | null) =>
  company ? `${name} (${company})` : name;

/** Ringkas `details` dari respons 400 backend (string atau objek per field). */
const summarizeDetails = (details: unknown): string | undefined => {
  if (!details) return undefined;
  if (typeof details === "string") return details;
  if (typeof details === "object") {
    const parts: string[] = [];
    const walk = (value: unknown, path: string) => {
      if (typeof value === "string") parts.push(path ? `${path}: ${value}` : value);
      else if (value && typeof value === "object") {
        Object.entries(value).forEach(([key, child]) => walk(child, path ? `${path}.${key}` : key));
      }
    };
    walk(details, "");
    return parts.length ? parts.join("; ") : undefined;
  }
  return undefined;
};

export default function DosForm({
  title,
  initialValue,
  rejectionReason,
  onSubmit,
  onSubmitError,
  onCancel,
}: DosFormProps) {
  const initialCustomer = initialValue?.customer ?? null;

  const [customerIdRef, setCustomerIdRef] = useState<number | null>(initialCustomer?.id ?? null);
  const [customerQuery, setCustomerQuery] = useState(
    initialCustomer ? customerLabel(initialCustomer.customer_name, initialCustomer.company_name) : ""
  );
  const [billTo, setBillTo] = useState(
    initialCustomer ? initialCustomer.company_name || initialCustomer.customer_name : ""
  );
  const [address, setAddress] = useState(initialCustomer?.address ?? "");
  const [phone, setPhone] = useState(initialCustomer?.phone ?? "");

  const [salesId, setSalesId] = useState<number | null>(initialValue?.sales_id ?? null);
  const [poNumber, setPoNumber] = useState(initialValue?.po_number ?? "");
  const [deliveryDate, setDeliveryDate] = useState(initialValue?.delivery_date ?? "");
  const [dueDate, setDueDate] = useState(initialValue?.due_date ?? "");
  const [checkedBy, setCheckedBy] = useState(initialValue?.checked_by ?? "");
  const [receivedBy, setReceivedBy] = useState(initialValue?.received_by ?? "");
  const [items, setItems] = useState<DosFormItem[]>(initialValue?.items ?? []);

  const [quotationId, setQuotationId] = useState<number | null>(initialValue?.quotation?.id ?? null);
  const [quotationOptions, setQuotationOptions] = useState<QuotationOption[]>([]);
  const [filling, setFilling] = useState(false);

  const [salesOptions, setSalesOptions] = useState<SalesOption[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [itemsErrors, setItemsErrors] = useState<ItemsErrors>({});
  const [itemsGeneralError, setItemsGeneralError] = useState<string | undefined>();

  useEffect(() => {
    const fetchSales = async () => {
      try {
        const response = await getSales({ page: 1, limit: 10 });
        setSalesOptions(response.data.salesList ?? []);
      } catch (error) {
        if (!isForbiddenError(error)) showError("Failed", "Failed to Load data.");
      }
    };

    fetchSales();
  }, []);

  useEffect(() => {
    // Backend hanya mengembalikan quotation milik Sales yang login.
    const fetchQuotations = async () => {
      try {
        const response = await getQuotation({ page: 1, limit: 100 });
        const list: QuotationOption[] = response.data.quotationList ?? [];
        setQuotationOptions(
          list.filter(
            (quotation) =>
              !isSalesEditLocked(quotation.status?.code) &&
              quotation.status?.code !== QUOTATION_CANCELLED_CODE
          )
        );
      } catch (error) {
        if (!isForbiddenError(error)) showError("Failed", "Failed to load quotations.");
      }
    };

    fetchQuotations();
  }, []);

  // Quotation yang sudah tertaut (edit) tetap tampil walau kini tidak memenuhi syarat.
  const linkedQuotation = initialValue?.quotation;
  const showLinkedQuotation =
    linkedQuotation && !quotationOptions.some((option) => option.id === linkedQuotation.id);

  const handleSelectCustomer = (customer: CustomerOption) => {
    setCustomerIdRef(customer.id);
    setCustomerQuery(customerLabel(customer.customerName, customer.companyName));
    setBillTo(customer.companyName ?? customer.customerName);
    setAddress(customer.address ?? "");
    setPhone(customer.phone ?? "");
    setErrors((prev) => {
      const { customer_id_ref, ...rest } = prev;
      return rest;
    });
  };

  const handleClearCustomer = () => {
    setCustomerIdRef(null);
    setCustomerQuery("");
    setBillTo("");
    setAddress("");
    setPhone("");
  };

  const handleFillFromQuotation = async () => {
    if (!quotationId) return;

    try {
      setFilling(true);
      const response = await getQuotationById(quotationId);
      const data = response.data.data ?? response.data;

      if (data.customer) {
        setCustomerIdRef(data.customer.id);
        setCustomerQuery(customerLabel(data.customer.customer_name, data.customer.company_name));
        setBillTo(data.customer.company_name || data.customer.customer_name || "");
        setAddress(data.customer.address ?? "");
        setPhone(data.customer.phone ?? "");
      }
      if (data.sales?.id) setSalesId(data.sales.id);

      setItems(
        (data.items ?? []).map((item: any) => ({
          description: item.description ?? "",
          qty: Number(item.qty),
          unit: item.unit ?? "",
          remark: "",
          product_id: item.product_id ?? null,
        }))
      );
    } catch (error) {
      if (!isForbiddenError(error)) {
        showError("Failed", await getApiErrorMessage(error, "Failed to load quotation."));
      }
    } finally {
      setFilling(false);
    }
  };

  const fields: DocumentFieldConfig<DocumentValue>[] = [
    { key: "bill_to", label: "Bill To", type: "readonly", column: 1, readOnlyFallback: "-" },
    { key: "address", label: "Address", type: "readonly", column: 1, readOnlyFallback: "-" },
    { key: "phone", label: "Phone", type: "readonly", column: 1, readOnlyFallback: "-" },
    { key: "delivery_date", label: "Delivery Date", type: "date", column: 1 },
    {
      key: "sales_id",
      label: "Sales",
      type: "select",
      column: 2,
      numeric: true,
      placeholderOption: { value: "", label: "Select Sales" },
      options: salesOptions.map((sales) => ({ value: sales.id, label: sales.name })),
    },
    { key: "po_number", label: "PO Number", type: "text", column: 2 },
    { key: "due_date", label: "Due Date", type: "date", column: 2 },
  ];

  const handleFieldChange = <K extends keyof DocumentValue>(key: K, value: DocumentValue[K]) => {
    switch (key) {
      case "delivery_date":
        setDeliveryDate(value as string);
        break;
      case "due_date":
        setDueDate(value as string);
        break;
      case "sales_id":
        setSalesId(value ? Number(value) : null);
        break;
      case "po_number":
        setPoNumber(value as string);
        break;
    }
  };

  const handleSave = async () => {
    const result = validateDosForm(
      {
        customer_id_ref: customerIdRef,
        sales_id: salesId,
        po_number: poNumber,
        delivery_date: deliveryDate,
        due_date: dueDate,
        checked_by: checkedBy,
        received_by: receivedBy,
      },
      items
    );

    setErrors(result.fieldErrors);
    setItemsErrors(result.itemsErrors);
    setItemsGeneralError(result.itemsGeneralError);

    if (result.hasError) {
      focusFirstError({
        fieldErrors: result.fieldErrors,
        itemsHasError: Object.keys(result.itemsErrors).length > 0 || !!result.itemsGeneralError,
        itemsSectionId: "dos-items-section",
      });
      return;
    }

    const payload: DosPayload = {
      customer_id_ref: customerIdRef as number,
      sales_id: salesId as number,
      po_number: poNumber.trim(),
      delivery_date: deliveryDate,
      due_date: dueDate,
      checked_by: checkedBy.trim(),
      received_by: receivedBy.trim(),
      quotation_id: quotationId,
      items: items.map((item) => ({
        description: item.description.trim(),
        qty: item.qty,
        unit: item.unit.trim(),
        remark: (item.remark ?? "").trim(),
        product_id: item.product_id ?? null,
      })),
    };

    try {
      setSubmitting(true);
      await onSubmit(payload);
    } catch (error) {
      if (!isForbiddenError(error)) {
        const message = await getApiErrorMessage(error, "Failed to save DOS.");
        const details = summarizeDetails(
          (error as { response?: { data?: { details?: unknown } } })?.response?.data?.details
        );
        showError(message, details);
      }
      onSubmitError?.(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-3xl bg-white p-6">
      <h2 className="mb-6 text-lg font-semibold">{title}</h2>

      {rejectionReason && (
        <div
          role="alert"
          className="mb-6 rounded-2xl border-2 border-red-300 bg-red-50 p-4 text-red-800">
          <p className="font-semibold">Rejection Reason</p>
          <p className="mt-1 whitespace-pre-line">{rejectionReason}</p>
        </div>
      )}

      <div className="mb-6 flex items-end gap-3">
        <div className="flex-1">
          <label htmlFor="dos-quotation" className="mb-1 block text-sm font-medium text-gray-600">
            Fill from Quotation
          </label>
          <select
            id="dos-quotation"
            value={quotationId ?? ""}
            onChange={(e) => setQuotationId(e.target.value ? Number(e.target.value) : null)}
            className={inputClass}>
            <option value="">Without quotation</option>
            {showLinkedQuotation && linkedQuotation && (
              <option value={linkedQuotation.id}>{linkedQuotation.quotation_no} (locked)</option>
            )}
            {quotationOptions.map((quotation) => (
              <option key={quotation.id} value={quotation.id}>
                {quotation.recipient_name
                  ? `${quotation.quotation_no} - ${quotation.recipient_name}`
                  : quotation.quotation_no}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={handleFillFromQuotation}
          disabled={!quotationId || filling || submitting}
          className="h-[38px] rounded-lg border border-blue-200 px-4 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50">
          {filling ? "Filling..." : "Fill"}
        </button>
      </div>

      <div id="field-customer_id_ref">
        <CustomerPicker
          query={customerQuery}
          onQueryChange={setCustomerQuery}
          onSelectCustomer={handleSelectCustomer}
          selectedCustomerId={customerIdRef}
          onClearSelection={handleClearCustomer}
        />
        {errors.customer_id_ref && (
          <p className="-mt-4 mb-4 text-xs text-red-500">{errors.customer_id_ref}</p>
        )}
      </div>

      <DocumentInformation
        value={{
          bill_to: billTo,
          address,
          phone,
          delivery_date: deliveryDate,
          due_date: dueDate,
          sales_id: salesId ?? "",
          po_number: poNumber,
        }}
        fields={fields}
        labelWidthClass="w-40"
        errors={errors}
        onChange={withErrorClear(handleFieldChange, setErrors)}
      />

      <div className="my-6 border-t border-gray-100" />

      <div id="dos-items-section">
        <h3 className="mb-3 text-sm font-semibold text-gray-700">DOS Item</h3>
        <DeliveryOrderItemTable items={items} onChange={setItems} />
        {itemsGeneralError && <p className="mt-2 text-xs text-red-500">{itemsGeneralError}</p>}
        {Object.keys(itemsErrors).length > 0 && (
          <div className="mt-2 space-y-1">
            {Object.entries(itemsErrors).map(([rowIndex, rowErrors]) => (
              <p key={rowIndex} className="text-xs text-red-500">
                Row {Number(rowIndex) + 1}: {Object.values(rowErrors).join(", ")}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="my-6 border-t border-gray-100" />

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label htmlFor="field-checked_by" className="mb-1 block text-sm text-gray-600">
            Checked By
          </label>
          <input
            id="field-checked_by"
            type="text"
            value={checkedBy}
            onChange={(e) => setCheckedBy(e.target.value)}
            placeholder="Input text"
            className={inputClass}
          />
          {errors.checked_by && <p className="mt-1 text-xs text-red-500">{errors.checked_by}</p>}
        </div>

        <div>
          <label htmlFor="field-received_by" className="mb-1 block text-sm text-gray-600">
            Received By
          </label>
          <input
            id="field-received_by"
            type="text"
            value={receivedBy}
            onChange={(e) => setReceivedBy(e.target.value)}
            placeholder="Input text"
            className={inputClass}
          />
          {errors.received_by && <p className="mt-1 text-xs text-red-500">{errors.received_by}</p>}
        </div>
      </div>

      <div className="mt-8 flex justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={submitting}
          className="rounded-xl border border-red-200 px-5 py-2.5 font-medium text-red-500 hover:bg-red-50 disabled:opacity-50">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={submitting}
          className="rounded-xl bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          {submitting ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}
